import { IconListCheck } from '@tabler/icons-react';
import { use } from 'react';

import { type PlanInput, PlanInputSchema, type PlanOutput } from '#plugin-chat/components/tools/plan/schema.ts';
import type { ToolProps } from '#plugin-chat/components/tools/Tool.ts';
import { ToolsContext } from '#plugin-chat/contexts/ToolsContext.ts';
import { ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { Tool } from '#plugin-chat/lib/tool/Tool.tsx';

/**
 * Structured planning tool. Agents call this before any multi-step workflow so the
 * plan is expressed as validated tool-call fields (summary, final grain, expected
 * output, ordered steps, assumptions) rather than JSON-shaped prose narration in a
 * regular assistant message.
 *
 * Built on the `<Tool>` component. The wire name (`plan`) is supplied
 * by the mounting agent's `tools={{ plan: <PlanTool/> }}` map; `<Tool>`
 * reads it from `ToolContext` and `<ToolGuidance>` reads it for the
 * "When to use" headers. Cross-tool references inside the description
 * are read from `<ToolsContext>` as backticked strings —
 * `{tools.showDashboard}`.
 *
 * The when-to/not-to-use bullets are supplied on the `description` prop via a
 * nested `<ToolGuidance>` (`<Tool>` has no separate `instructions` channel);
 * the Procedure list is carried as its `children`.
 */
export function PlanTool(props: ToolProps) {
  const tools = use(ToolsContext);
  return (
    <Tool<PlanInput, PlanOutput>
      collapsible
      color="teal"
      description={
        <ToolGuidance
          description="Declare the multi-step plan for answering the user's question. Call this before any other tool when the answer requires two or more tool calls."
          whenNotToUse={[
            'Trivial single-call answers (one lookup, one clarifying question, a single dashboard of already-staged data).',
            'Questions a single `transformDataset` or `showDashboard` call can answer on its own.',
          ]}
          whenToUse={[
            'Any question that will require two or more tool calls to answer.',
            'Ambiguous or multi-step requests where stating the approach up front lets the user verify intent before data fetches start.',
          ]}
        >
          <header>Procedure</header>
          <ol>
            <li>
              Call this tool FIRST, before any data-fetching or processing tools. A plan after the fact is a failure
              mode.
            </li>
            <li>
              Describe the <em>why</em> and the <em>expected outcome</em> in each step.
            </li>
            <li>
              Populate <code>finalGrain</code> and <code>expectedOutput</code> on every call. The plan must collapse raw
              data down to <code>finalGrain</code>, and the last step typically produces <code>expectedOutput</code> via
              `showDashboard`.
            </li>
            <li>
              Surface assumptions you are making (entity names, time ranges, unit conventions, categorical encodings) in{' '}
              <code>assumptions</code>.
            </li>
            <li>After this tool returns, execute the plan from start to finish.</li>
          </ol>
        </ToolGuidance>
      }
      execute={() => ({
        success: true,
        flowSignal: {
          appendMessages: [
            {
              role: 'user',
              content: `Proceed to execute the plan.

Call ${tools.DescribeViews} for views you plan on querying. Do not call ${tools.DescribeViews} again for views previously described with ${tools.DescribeViews}.`,
            },
          ],
        },
      })}
      icon={IconListCheck}
      message={{
        // Keep the call line minimal — the rendered plan in `execute`'s
        // result message already carries every field the LLM needs, so
        // restating the plan body here would duplicate it inside the
        // transcript's tool-call record.
        call: () => (
          <p>
            You called the <code>plan</code> tool to record an execution plan (full plan below).
          </p>
        ),
        result: ({ args }) => (
          <>
            <strong>
              Execution plan recorded ({args.steps?.length} step{args.steps?.length === 1 ? '' : 's'}, final grain:{' '}
              {args.finalGrain}).
            </strong>
            <p>{args.summary}</p>
            <header>Final grain</header>
            <p>{args.finalGrain}</p>
            <header>Expected output</header>
            <p>{args.expectedOutput}</p>
            <header>Steps</header>
            <ol>
              {args.steps?.map((step) => (
                <li key={step.description}>
                  {step.description}
                  {step.tool ? (
                    <>
                      {' '}
                      (via <code>{step.tool}</code>)
                    </>
                  ) : null}
                </li>
              ))}
            </ol>
            {args.assumptions?.length ? (
              <>
                <header>Assumptions</header>
                <ul>
                  {args.assumptions.map((assumption) => (
                    <li key={assumption}>{assumption}</li>
                  ))}
                </ul>
              </>
            ) : null}
          </>
        ),
      }}
      parameters={PlanInputSchema}
      {...props}
    />
  );
}
